import type { Vector3 } from "@pax/basemap";

/**
 * The globe's camera, after the Earth budget spike's viewer (`spikes/earth-budget/viewer`).
 *
 * The camera hangs at altitude h above the target T, a point on the unit sphere, looking straight
 * down. Its heading is the screen's up direction U, tangent at T, held as a gyroscope holds its
 * direction: wherever T moves, U is carried along without turning. A compass turns U back toward
 * north at a rate proportional to how far off it is and to how well north is defined there,
 * cos(latitude). So away from the poles north stays up, and over a pole the map glides across
 * without flipping, then rights itself. Zoom is log2 of how much closer the camera is than at zoom 0.
 *
 * The drawn surface is a sphere tangent to the world at T, with curvature k from 1 (the globe itself)
 * to 0 (the plane tangent at T). A point is drawn at its true great-circle distance ρ from T and its
 * true bearing, measured along this sphere: at k = 1 that is the globe, at k = 0 the azimuthal
 * equidistant map. Every surface in between is a whole sphere, so the unfurl never folds over
 * itself. Curvature falls as the camera descends, k = min(1, h / UNFURL_ALTITUDE).
 *
 * Positions are relative to T, so deep zooms keep their precision. Directions are the world's, x
 * toward 0° 0° and z north.
 */

/** Lens: vertical field of view. */
const FOV = (40 * Math.PI) / 180;
/** Altitude at zoom 0, in world radii. */
const ALTITUDE_AT_ZOOM_0 = 3;
/** Below this altitude (world radii; about 1,700 km on Earth) the globe begins to unfurl. */
const UNFURL_ALTITUDE = 0.265;
/** Seconds for the compass to take a heading error at the equator down by a factor of e. */
const COMPASS_SECONDS = 0.6;

export class GlobeCamera {
  target: Vector3;
  /** The screen's up direction: a unit vector tangent to the sphere at the target. */
  up: Vector3;
  zoom = 0;
  width = 1;
  height = 1;
  // Derived by update():
  east: Vector3 = [0, 1, 0];
  north: Vector3 = [0, 0, 1];
  altitude = ALTITUDE_AT_ZOOM_0;
  curvature = 1;
  /** Column by column, for positions relative to T: screen x, y and depth, for the pole rods. */
  viewProj = new Float64Array(16);

  constructor(target: Vector3, up?: Vector3) {
    this.target = normalize(target);
    this.up = tangent(up ?? northAt(this.target)[0], this.target);
    this.update();
  }

  /** The heading at `to` if the camera moved there from the target along the great circle between. */
  transported(to: Vector3): Vector3 {
    const axis = cross(this.target, to);
    const sin = Math.hypot(...axis);
    if (sin < 1e-300) return tangent(this.up, to);
    const angle = Math.atan2(sin, dot(this.target, to));
    return tangent(rotate(this.up, scale(axis, 1 / sin), angle), to);
  }

  /** Moves the target to `to`, carrying the heading along. */
  moveTo(to: Vector3) {
    const next = normalize(to);
    this.up = this.transported(next);
    this.target = next;
    this.update();
  }

  /**
   * Turns the heading toward north for `dt` seconds. Heading error ψ decays as
   * dψ/dt = −ψ · cos(lat) / τ, so near a pole, where north is barely defined, the compass barely pulls.
   */
  compass(dt: number) {
    const strength = northAt(this.target)[1];
    if (strength < 1e-12) return;
    const turn = this.headingError() * (1 - Math.exp((-dt * strength) / COMPASS_SECONDS));
    if (Math.abs(turn) < 1e-15) return;
    this.up = tangent(rotate(this.up, this.target, turn), this.target);
    this.update();
  }

  /** How far the heading is from north, in radians (0 when north is up). */
  headingError(): number {
    const [toward] = northAt(this.target);
    return Math.atan2(dot(cross(this.up, toward), this.target), dot(this.up, toward));
  }

  clone(): GlobeCamera {
    const copy = new GlobeCamera(this.target, this.up);
    copy.zoom = this.zoom;
    copy.width = this.width;
    copy.height = this.height;
    copy.update();
    return copy;
  }

  /** Focal length in pixels. */
  get focal(): number {
    return this.height / 2 / Math.tan(FOV / 2);
  }

  /** Screen pixels a radian of the world spans straight below the camera. */
  get pixelsPerRadian(): number {
    return this.focal / this.altitude;
  }

  update() {
    [this.east, this.north] = frameOf(this.target, this.up);
    this.altitude = ALTITUDE_AT_ZOOM_0 * 2 ** -this.zoom;
    this.curvature = Math.min(1, this.altitude / UNFURL_ALTITUDE);
    const h = this.altitude;
    const k = this.curvature;
    // Every drawn point lies on or below the tangent plane at T, so none is nearer than h; and the
    // horizon, the deepest visible point, is h / (1 + k h) below that plane.
    const near = h * (1 - 1e-6);
    const far = (h + h / (1 + k * h)) * (1 + 1e-6);
    const f = 1 / Math.tan(FOV / 2);
    const a = f / (this.width / this.height);
    const c = (far + near) / (near - far);
    const d = (2 * far * near) / (near - far);
    const t = this.target;
    const [e, n] = [this.east, this.north];
    this.viewProj.set([
      a * e[0],
      f * n[0],
      c * t[0],
      -t[0],
      a * e[1],
      f * n[1],
      c * t[1],
      -t[1],
      a * e[2],
      f * n[2],
      c * t[2],
      -t[2],
      0,
      0,
      -c * h + d,
      h,
    ]);
  }

  /** Where world point `p` is drawn, relative to the target (T unless given). */
  place(p: Vector3, target = this.target): Vector3 {
    const cos = dot(p, target);
    const along = sub(p, scale(target, cos));
    const sin = Math.hypot(...along);
    if (sin < 1e-300) {
      return cos > 0 ? [0, 0, 0] : scale(target, -surface(Math.PI, this.curvature)[1]);
    }
    const [across, drop] = surface(Math.atan2(sin, cos), this.curvature);
    return sub(scale(along, across / sin), scale(target, drop));
  }

  /** Where world point `p` lands on screen if the camera moved to look down at `target` instead. */
  screenOf(p: Vector3, target: Vector3): [number, number] {
    const [e, n] = frameOf(target, this.transported(target));
    const r = this.place(p, target);
    const depth = this.altitude - dot(r, target);
    return [
      this.width / 2 + (this.focal * dot(r, e)) / depth,
      this.height / 2 - (this.focal * dot(r, n)) / depth,
    ];
  }

  /**
   * The world point drawn at screen pixel (sx, sy), or null where the ray misses the surface or
   * meets it beyond the point opposite T. Closed form: the ray meets k|a|² + 2a·T = 0 (the drawn
   * sphere, written so that k = 0 is the plane), and the hit's chord from T gives back ρ. The shader
   * does the same for every pixel.
   */
  pick(sx: number, sy: number): Vector3 | null {
    const t = this.target;
    const h = this.altitude;
    const k = this.curvature;
    const d = normalize(
      add(
        scale(t, -1),
        add(
          scale(this.east, (sx - this.width / 2) / this.focal),
          scale(this.north, (this.height / 2 - sy) / this.focal),
        ),
      ),
    );
    const b = dot(d, t) * (1 + k * h);
    const c = h * (k * h + 2);
    const disc = b * b - k * c;
    if (b >= 0 || disc < 0) return null;
    const s = c / (-b + Math.sqrt(disc));
    const a = add(scale(t, h), scale(d, s));
    const chord = Math.hypot(...a);
    const rho = chord * asinc((k * chord) / 2);
    if (rho > Math.PI) return null;
    const along = sub(a, scale(t, dot(a, t)));
    const length = Math.hypot(...along);
    if (length < 1e-300) return t;
    return add(scale(t, Math.cos(rho)), scale(along, Math.sin(rho) / length));
  }

  /** The world point drawn nearest (sx, sy) along the line from the screen's center. */
  pickNearest(sx: number, sy: number): Vector3 {
    const hit = this.pick(sx, sy);
    if (hit) return hit;
    const cx = this.width / 2;
    const cy = this.height / 2;
    let inside = 0;
    let outside = 1;
    for (let step = 0; step < 48; step++) {
      const middle = (inside + outside) / 2;
      if (this.pick(cx + (sx - cx) * middle, cy + (sy - cy) * middle)) inside = middle;
      else outside = middle;
    }
    return this.pick(cx + (sx - cx) * inside, cy + (sy - cy) * inside) ?? this.target;
  }

  /**
   * Turns the globe so world point `p` lands at screen pixel (sx, sy): the target is solved by damped
   * least squares (Levenberg–Marquardt) over the plane tangent to it. Where the geometry can put `p`
   * exactly there it does, to a thousandth of a pixel; where it can't, `p` lands as near as it can.
   */
  grab(p: Vector3, sx: number, sy: number) {
    let target = this.target;
    let [x, y] = this.screenOf(p, target);
    let error = Math.hypot(sx - x, sy - y);
    let damping = 1e-3;
    for (let step = 0; step < 30 && error > 1e-3; step++) {
      const [e1, e2] = frameOf(target, this.transported(target));
      const eps = (1 / this.pixelsPerRadian) * 1e-2;
      const [x1, y1] = this.screenOf(p, normalize(add(target, scale(e1, eps))));
      const [x2, y2] = this.screenOf(p, normalize(add(target, scale(e2, eps))));
      const j11 = (x1 - x) / eps;
      const j12 = (x2 - x) / eps;
      const j21 = (y1 - y) / eps;
      const j22 = (y2 - y) / eps;
      const rx = sx - x;
      const ry = sy - y;
      // (JᵀJ + λ·diag JᵀJ) δ = Jᵀr
      const a11 = j11 * j11 + j21 * j21;
      const a12 = j11 * j12 + j21 * j22;
      const a22 = j12 * j12 + j22 * j22;
      const g1 = j11 * rx + j21 * ry;
      const g2 = j12 * rx + j22 * ry;
      const m11 = a11 * (1 + damping);
      const m22 = a22 * (1 + damping);
      const det = m11 * m22 - a12 * a12;
      if (!(Math.abs(det) > 0)) break;
      const d1 = (g1 * m22 - a12 * g2) / det;
      const d2 = (m11 * g2 - a12 * g1) / det;
      const next = normalize(add(target, add(scale(e1, d1), scale(e2, d2))));
      const [nx, ny] = this.screenOf(p, next);
      const nextError = Math.hypot(sx - nx, sy - ny);
      if (nextError < error) {
        target = next;
        [x, y, error] = [nx, ny, nextError];
        damping /= 3;
      } else {
        damping *= 3;
        if (damping > 1e8) break;
      }
    }
    this.moveTo(target);
  }

  /** Changes zoom with the ground under (sx, sy), or nearest it, held where it is. */
  zoomAt(zoom: number, sx: number, sy: number) {
    const ground = this.pickNearest(sx, sy);
    this.zoom = zoom;
    this.update();
    this.grab(ground, sx, sy);
  }
}

/** The zoom at which a globe takes up `share` of a frame's shorter side. */
export function zoomShowing(share: number, width: number, height: number): number {
  const focal = height / 2 / Math.tan(FOV / 2);
  const radius = (share * Math.min(width, height)) / 2;
  // The limb is where the line of sight grazes the sphere: from distance D, at angle asin(1 / D).
  const distance = 1 / Math.sin(Math.atan(radius / focal));
  return Math.log2(ALTITUDE_AT_ZOOM_0 / (distance - 1));
}

/** The screen's right and up directions at `t` for heading `up`. */
function frameOf(t: Vector3, up: Vector3): [Vector3, Vector3] {
  return [cross(up, t), up];
}

/** `v` made tangent to the sphere at `t` and unit length. */
function tangent(v: Vector3, t: Vector3): Vector3 {
  return normalize(sub(v, scale(t, dot(v, t))));
}

/**
 * North at `t`: the unit direction toward the north pole along the surface, and how well defined it
 * is, cos(latitude). At a pole it's zero and the direction is arbitrary.
 */
function northAt(t: Vector3): [Vector3, number] {
  const raw = sub([0, 0, 1], scale(t, t[2]));
  const length = Math.hypot(...raw);
  if (length < 1e-12) return [tangent([1, 0, 0], t), 0];
  return [scale(raw, 1 / length), length];
}

/**
 * Where a point at great-circle distance ρ from T sits on the drawn sphere of curvature k: how far
 * across (along its bearing) and how far below the tangent plane, written to stay exact as k → 0.
 */
function surface(rho: number, k: number): [number, number] {
  const phi = k * rho;
  if (Math.abs(phi) < 1e-4) {
    return [rho * (1 - (phi * phi) / 6), ((rho * phi) / 2) * (1 - (phi * phi) / 12)];
  }
  const half = Math.sin(phi / 2);
  return [(rho * Math.sin(phi)) / phi, (rho * 2 * half * half) / phi];
}

/** asin(x) / x, exact near 0. */
function asinc(x: number): number {
  if (Math.abs(x) < 1e-4) return 1 + (x * x) / 6;
  return Math.asin(Math.min(1, x)) / x;
}

/** Turns `v` by `angle` about unit axis `axis` (Rodrigues). */
export function rotate(v: Vector3, axis: Vector3, angle: number): Vector3 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const kv = cross(axis, v);
  const kd = dot(axis, v) * (1 - c);
  return normalize([
    v[0] * c + kv[0] * s + axis[0] * kd,
    v[1] * c + kv[1] * s + axis[1] * kd,
    v[2] * c + kv[2] * s + axis[2] * kd,
  ]);
}

export const dot = (a: Vector3, b: Vector3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross = (a: Vector3, b: Vector3): Vector3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const add = (a: Vector3, b: Vector3): Vector3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const sub = (a: Vector3, b: Vector3): Vector3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const scale = (a: Vector3, s: number): Vector3 => [a[0] * s, a[1] * s, a[2] * s];
export const normalize = (a: Vector3): Vector3 => scale(a, 1 / (Math.hypot(...a) || 1));
